import { useState, useEffect, useContext } from "react";
import { FileUploader } from "react-drag-drop-files";
import { AuthContext } from "../../../Context/AuthContext";
import axios from "axios";
import Preloader from "../../../shared/PreLoaders";
import { logActivity } from "../../../utils/LogActivity";
import { Authorization } from "../../../Context/Authorization";
import { toast } from "react-toastify";

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s", cursor: "pointer",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };

const GroupLoan = () => {
  const { userId, selectedJfmcType } = useContext(AuthContext);
  const Api = process.env.REACT_APP_API;
  const [loading, setLoading] = useState(false);

  const [vfcNames, setVfcNames] = useState([]);
  const [selectedVfc, setSelectedVfc] = useState("");
  const [shgs, setShgs] = useState([]);
  const [selectedShg, setSelectedShg] = useState("");
  const [members, setMembers] = useState([]);
  const [loanMembers, setLoanMembers] = useState([]);
  const [loanTypes, setLoanTypes] = useState([]);
  const [selectedLoanType, setSelectedLoanType] = useState("");
  const [installments, setInstallments] = useState([]);
  const [selectedInstallment, setSelectedInstallment] = useState("");
  const [selectedCashorBank, setSelectedCashorBank] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [uploadDocument, setUploadDocument] = useState(null);
  const [submitMessage, setSubmitMessage] = useState("");

  const fetchVfcNames = async (jfmcTypeId) => {
    try {
      setLoading(true);
      const res = await axios.post(`${Api}/api/vfcs/names`, { jfmcTypeId }, Authorization());
      setVfcNames(res.data.vfcs || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedJfmcType) fetchVfcNames(selectedJfmcType._id);
  }, [selectedJfmcType]);

  useEffect(() => {
    const load = async () => {
      try {
        const config = Authorization();
        const loanRes = await fetch(`${Api}/api/loan-types`, { headers: { ...config.headers } });
        const loanData = await loanRes.json();
        setLoanTypes(loanData.loanTypes || []);
        const instRes = await axios.get(`${Api}/api/installments`, config);
        setInstallments(instRes.data || []);
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, []);

  const handleVfcChange = async (e) => {
    const vfcId = e.target.value;
    setSelectedVfc(vfcId);
    try {
      setLoading(true);
      const res = await axios.post(`${Api}/api/shgs/getShgsByVFC`, { vfcId }, Authorization());
      setShgs(res.data.shgs || []);
      setMembers([]);
      setLoanMembers([]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleShgChange = async (e) => {
    const shgId = e.target.value;
    setSelectedShg(shgId);
    try {
      setLoading(true);
      const res = await fetch(`${Api}/api/shgMember/members`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...Authorization().headers },
        body: JSON.stringify({ shgId })
      });
      const data = await res.json();
      setMembers(data.members || []);
      setLoanMembers([]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const addMember = (m) => {
    if (loanMembers.find((x) => x.memberId === m.member._id)) return;
    setLoanMembers((prev) => [...prev, { memberId: m.member._id, name: m.member.name, amount: "", purpose: "" }]);
  };

  const removeMember = (id) => {
    setLoanMembers((prev) => prev.filter((x) => x.memberId !== id));
  };

  const updateMember = (index, key, value) => {
    const copy = [...loanMembers];
    copy[index][key] = value;
    setLoanMembers(copy);
  };

  const handleFileUpload = (file) => { setUploadDocument(file); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!loanMembers.length) { alert("Select at least one member"); return; }
    const totalAmount = loanMembers.reduce((sum, m) => sum + Number(m.amount || 0), 0);
    try {
      const form = new FormData();
      if (uploadDocument) form.append("uploadDocument", uploadDocument);
      form.append("vfcId", selectedVfc);
      form.append("loanType", selectedLoanType);
      form.append("amount", totalAmount);
      form.append("shgId", selectedShg);
      form.append("date", selectedDate);
      form.append("installment", selectedInstallment);
      form.append("cashOrBank", selectedCashorBank);
      form.append("groupType", "Group");
      form.append("subLoans", JSON.stringify(
        loanMembers.map((m, i) => ({ memberId: m.memberId, subLoanId: `subLoan_${i}`, disbursedAmount: m.amount, status: "AL" }))
      ));
      const res = await fetch(`${Api}/api/loans`, {
        method: "POST",
        headers: { ...Authorization().headers },
        body: form
      });
      if (!res.ok) throw new Error();
      toast.success("Loan Created Successfully");
      setSubmitMessage("Loan Created Successfully");
      await logActivity(userId, "New Group Loan Created", "Group Loan", Api);
      setLoanMembers([]);
    } catch (err) {
      console.error(err);
      setSubmitMessage("Error creating loan");
      toast.error("Error creating loan");
    }
  };

  return (
    <div>
      {loading && <Preloader loading />}

      {!selectedJfmcType ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "48px 24px", textAlign: "center" }}>
          <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>JFMC Type not selected</div>
          <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar</div>
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflow: "hidden" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
            <span style={{ width: 30, height: 30, background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 15 }}>
              <i className="mdi mdi-account-group-outline" />
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Group Loan Form</span>
          </div>
          <div style={{ padding: "20px 24px" }}>
            {submitMessage && (
              <div style={{ marginBottom: 16, padding: "10px 16px", background: "#E8F5E9", border: "1px solid #C8E6C9", borderRadius: 8, fontSize: 13, color: "#1B5E20" }}>
                {submitMessage}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
                {/* Left column */}
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div>
                    <label style={labelStyle}>JFMC Name</label>
                    <select value={selectedVfc} onChange={handleVfcChange} style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}>
                      <option value="">Select JFMC</option>
                      {vfcNames.map((v) => <option key={v._id} value={v._id}>{v.vfcName}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>SHG</label>
                    <select value={selectedShg} onChange={handleShgChange} style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}>
                      <option value="">Select SHG</option>
                      {shgs.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Loan Type</label>
                    <select value={selectedLoanType} onChange={(e) => setSelectedLoanType(e.target.value)} style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}>
                      <option value="">Select Loan Type</option>
                      {loanTypes.map((loan) => <option key={loan._id} value={loan._id}>{loan.loanName}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Loan Date</label>
                    <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")} />
                  </div>
                  <div>
                    <label style={labelStyle}>Installment</label>
                    <select value={selectedInstallment} onChange={(e) => setSelectedInstallment(e.target.value)} style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}>
                      <option value="">Select Installment</option>
                      {installments.map((i) => <option key={i._id} value={i._id}>{i.installmentNumber}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Cash / Bank</label>
                    <select value={selectedCashorBank} onChange={(e) => setSelectedCashorBank(e.target.value)} style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}>
                      <option value="">Select</option>
                      <option value="cash">Cash</option>
                      <option value="bank">Bank</option>
                    </select>
                  </div>
                </div>

                {/* Right column */}
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Upload Document</label>
                    <FileUploader handleChange={handleFileUpload} types={["jpg", "png", "pdf"]} />
                  </div>
                </div>
              </div>

              {/* Member Selection */}
              {members.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#2D3748", marginBottom: 10, paddingBottom: 8, borderBottom: "1px solid #EDF2F7" }}>
                    Select Members
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {members.map((m) => (
                      <div key={m.member._id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "#F7FAFC", borderRadius: 8, border: "1px solid #EDF2F7" }}>
                        <span style={{ fontSize: 13, color: "#1A202C", fontWeight: 500 }}>{m.member.name}</span>
                        <button type="button" onClick={() => addMember(m)}
                          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                          <i className="mdi mdi-account-multiple-plus" /> Add for Loan
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Loan Members */}
              {loanMembers.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#2D3748", marginBottom: 10, paddingBottom: 8, borderBottom: "1px solid #EDF2F7" }}>
                    Loan Members ({loanMembers.length})
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {loanMembers.map((m, i) => (
                      <div key={m.memberId} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr auto", gap: 12, alignItems: "center", padding: "10px 14px", background: "#F7FAFC", borderRadius: 8, border: "1px solid #EDF2F7" }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1A202C" }}>{m.name}</span>
                        <input type="number" placeholder="Amount" value={m.amount}
                          onChange={(e) => updateMember(i, "amount", e.target.value)}
                          style={{ ...inputStyle, padding: "7px 10px" }}
                          onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")} />
                        <input placeholder="Purpose" value={m.purpose}
                          onChange={(e) => updateMember(i, "purpose", e.target.value)}
                          style={{ ...inputStyle, padding: "7px 10px" }}
                          onFocus={(e) => (e.target.style.borderColor = "#4CAF50")} onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")} />
                        <button type="button" onClick={() => removeMember(m.memberId)}
                          style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 12px", background: "#FFF5F5", color: "#C62828", border: "1px solid #FFCDD2", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                          <i className="mdi mdi-account-multiple-minus" /> Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button type="submit"
                style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 24px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)", color: "#fff", border: "none", borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: "pointer", boxShadow: "0 3px 10px rgba(46,125,50,0.3)" }}>
                <i className="mdi mdi-file-check" /> Submit Loan
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupLoan;
